import {LocalStorage} from '@stripe-internal/stripe-cookies';
import {Observable, Subscription} from 'rxjs';
import moment from 'moment';
import {CONVERSATION_STORAGE_PREFIX} from './conversation';

export const storage = new LocalStorage();

// update the schema version if the StoredState interface changes
export const STORAGE_SCHEMA_VERSION = 3;

export interface StoredState<T> {
  version: number;
  timestamp: number;
  data: T;
  schemaVersion?: number;
}

export interface SyncableStorage<T> {
  // the key used to store the data in localStorage
  key: () => string;
  // the version of the data stored in localStorage
  version: () => number;
  // an observable of values to store in localStorage
  // when new values are emitted, they will be written
  data: () => Observable<T>;
  // called when a new value is received from storage
  // and the value in storage has a newer version
  nextFromStorage: (storedState: StoredState<T>) => void;
}

// how old stored data should be allowed to be
export interface STORAGE_TTL {
  value: number;
  unit: 'minutes' | 'hours' | 'days';
}

export const loadStateFromStorage = <T>(
  storageKey: string,
): StoredState<T> | null => {
  if (!storage.isStorageAvailable()) {
    return null;
  }

  const stored = storage.get(storageKey);
  if (!stored) {
    return null;
  }

  try {
    const state = JSON.parse(stored) as StoredState<T>;

    if (state.schemaVersion !== STORAGE_SCHEMA_VERSION) {
      return null;
    }

    return state;
  } catch (e) {}

  return null;
};

export const syncStorage = <T>(syncable: SyncableStorage<T>): Subscription => {
  const sub = new Subscription();

  if (!storage.isStorageAvailable()) {
    return sub;
  }

  // anytime new data is emitted, store it
  sub.add(
    syncable.data().subscribe((value: T) => {
      try {
        const storedState: StoredState<T> = {
          version: syncable.version(),
          data: value,
          timestamp: Date.now(),
          schemaVersion: STORAGE_SCHEMA_VERSION,
        };
        storage.set(syncable.key(), JSON.stringify(storedState));
      } catch (e) {}
    }),
  );

  const storageListener = (event: StorageEvent) => {
    if (event.key !== syncable.key() || !event.newValue) {
      return;
    }

    try {
      const storedState = JSON.parse(event.newValue) as StoredState<T>;

      if (storedState.schemaVersion !== STORAGE_SCHEMA_VERSION) {
        return;
      }

      // if we receive a storage event and the stored data is newer
      // than ours, emit an update from storage
      if (storedState.version > syncable.version()) {
        syncable.nextFromStorage(storedState);
      }
    } catch (e) {}
  };

  window.addEventListener('storage', storageListener);

  // when the subscription is unsubscribed, remove the storage listener
  sub.add(() => {
    window.removeEventListener('storage', storageListener);
  });

  return sub;
};

// clears all stored conversations
// if a ttl is specified, only clears conversations
// older than the ttl are cleared
export const clearStorage = ({
  prefix = CONVERSATION_STORAGE_PREFIX,
  ttl,
}: {prefix?: string; ttl?: STORAGE_TTL} = {}): void => {
  // not checking for storage availability here because
  // we want to attempt to clear storage if we would have
  // otherwise got an error about it being full

  // offset here represents how old stored data should be allowed to be
  // compared to the current time
  let offset: number | undefined;
  if (ttl) {
    offset = moment(Date.now()).subtract(ttl.value, ttl.unit).valueOf();
  }

  try {
    // scoping to only conversations; we don't want to allow any prefix
    // being passed to clear outside of that scope
    const keys = storage.getKeyMatches(`${CONVERSATION_STORAGE_PREFIX}_*`);
    keys.forEach((key) => {
      if (!key.startsWith(prefix)) {
        return;
      }

      if (!offset) {
        // if no ttl specified, remove it
        storage.remove(key);
        return;
      }

      const stored = storage.get(key);
      if (!stored) {
        return;
      }

      const data = JSON.parse(stored);
      if (!('timestamp' in data)) {
        // if no timestamp in data specified, remove it
        storage.remove(key);
        return;
      }

      if (data.timestamp < offset) {
        // if stored data older than ttl, remove it
        storage.remove(key);
      }
    });
  } catch (e) {}
};
